/**
 * Demande d'acceptation présentée au destinataire.
 *
 * Le bouton « Accepter » déclenche l'ouverture du sélecteur de destination. Il doit donc
 * appeler directement la session, sans passer par un `await` intermédiaire : l'autorisation
 * d'écriture exige une activation utilisateur transitoire, consommée dès qu'on attend
 * autre chose.
 */

import { useFormatters, useT } from '../i18n/index.js';
import { useAppStore } from '../store.js';

/** Nombre de fichiers détaillés avant de résumer le reste. */
const VISIBLE_FILES = 12;

interface IncomingRequestProps {
  onAccept: () => void;
  onReject: () => void;
}

export function IncomingRequest({
  onAccept,
  onReject,
}: IncomingRequestProps): React.JSX.Element | null {
  const incoming = useAppStore((state) => state.incoming);
  const translator = useT();
  const format = useFormatters();

  if (!incoming) return null;

  const { files, totalBytes } = incoming;
  const hidden = files.length - VISIBLE_FILES;

  return (
    <div className="card">
      <h2>{translator.plural('incoming.title', files.length)}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {translator.t('incoming.total', { size: format.bytes(totalBytes) })}
      </p>

      <ul className="file-list">
        {files.slice(0, VISIBLE_FILES).map((file) => (
          <li className="file-item" key={file.id}>
            <span className="file-name" title={file.name}>
              {file.name}
            </span>
            <span className="file-size">{format.bytes(file.size)}</span>
          </li>
        ))}
        {hidden > 0 && (
          <li className="file-item">
            <span className="file-name muted">{translator.plural('incoming.andMore', hidden)}</span>
          </li>
        )}
      </ul>

      <div className="row" style={{ marginTop: '1.25rem' }}>
        <button type="button" className="btn btn-primary" onClick={onAccept}>
          {translator.t('incoming.accept')}
        </button>
        <button type="button" className="btn" onClick={onReject}>
          {translator.t('incoming.reject')}
        </button>
      </div>
    </div>
  );
}
